/**
 * Copyright (c) 2025, Ouster, Inc.
 * All rights reserved.
 */
import { RawDriveInfo } from '@ouster/webviz-react-components';

import * as Sentry from '@sentry/react';
import {
  action,
  Action as EzPzAction,
  Thunk as EzPzThunk,
  thunk,
} from 'easy-peasy';

import { UserLookupResponse } from '../components/adminDashboard/types';
import { BASE_ADMIN_DASHBOARD_API } from '../utils/constants';
import { fetchWithAuthV2 } from '../utils/requests';

/// ///////////////////////////////////////////////////////////////////////////
// State Model
/// ///////////////////////////////////////////////////////////////////////////

// Extend RawDriveInfo to include replicas
type RawDriveInfoWithRegions = RawDriveInfo & {
  regions: { region: string }[];
};
type DriveInfoWithRegions = RawDriveInfo & {
  regions: string[];
};
export type SlamStatusResponse = {
  organization_id: number;
  slam_enabled: boolean;
};

export type ReloadSensorMappingResponse = {
  message: string;
  entries_loaded: number;
  mapping_sample: Record<string, any>;
};

/* 
  We use user's range preference in local storage
*/
const savedTimeRange = localStorage.getItem('timeRange');

export type State = {
  summaryTimeRange: number;
  orgPage: number;
  orgPageSize: number;
  userLookupResult: UserLookupResponse | null;
  recordingLookupResult: DriveInfoWithRegions | null;
  isLoadingUserLookup: boolean;
  isLoadingRecordingLookup: boolean;
  compareUsersOrgsResult: {
    missing_organizations_in_uploader: Array<{
      org_name: string;
      tenant_id: string;
    }>;
    missing_users_in_uploader: Array<{
      email: string;
      identity_id: string;
    }>;
  } | null;
  hwVersionUpdateProgress: {
    totalUpdated: number;
    currentPage: number;
    isInProgress: boolean;
    progressPercentage: number;
    lastProcessedId: number;
  };
  verifiedEmailSyncProgress: {
    totalUpdated: number;
    currentPage: number;
    isInProgress: boolean;
    progressPercentage: number;
  };
};

export const defaultState: State = {
  summaryTimeRange: savedTimeRange ? parseInt(savedTimeRange, 10) : 7,
  orgPage: 0,
  orgPageSize: 25,
  userLookupResult: null,
  isLoadingUserLookup: false,
  recordingLookupResult: null,
  isLoadingRecordingLookup: false,
  compareUsersOrgsResult: null,
  hwVersionUpdateProgress: {
    totalUpdated: 0,
    currentPage: 0,
    isInProgress: false,
    progressPercentage: 0,
    lastProcessedId: 0,
  },
  verifiedEmailSyncProgress: {
    totalUpdated: 0,
    currentPage: 0,
    isInProgress: false,
    progressPercentage: 0,
  },
};

export interface AdminDashboardStateModel extends State {
  setSummaryTimeRange: EzPzAction<AdminDashboardStateModel, number>;
  setOrgPage: EzPzAction<AdminDashboardStateModel, number>;
  setOrgPageSize: EzPzAction<AdminDashboardStateModel, number>;
  setUserLookupResult: EzPzAction<
    AdminDashboardStateModel,
    UserLookupResponse | null
  >;
  setRecordingLookupResult: EzPzAction<
    AdminDashboardStateModel,
    DriveInfoWithRegions | null
  >;
  setIsLoadingUserLookup: EzPzAction<AdminDashboardStateModel, boolean>;
  setIsLoadingRecordingLookup: EzPzAction<AdminDashboardStateModel, boolean>;
  setHwVersionUpdateProgress: EzPzAction<
    AdminDashboardStateModel,
    {
      totalUpdated: number;
      currentPage: number;
      isInProgress: boolean;
      progressPercentage: number;
      lastProcessedId: number;
    }
  >;
  setVerifiedEmailSyncProgress: EzPzAction<
    AdminDashboardStateModel,
    {
      totalUpdated: number;
      currentPage: number;
      isInProgress: boolean;
      progressPercentage: number;
    }
  >;
  setCompareUsersOrgsResult: EzPzAction<
    AdminDashboardStateModel,
    {
      missing_organizations_in_uploader: Array<{
        org_name: string;
        tenant_id: string;
      }>;
      missing_users_in_uploader: Array<{
        email: string;
        identity_id: string;
      }>;
    } | null
  >;
  updateOrgAllowedSpace: EzPzThunk<
    AdminDashboardStateModel,
    { allowed_space: number; organization_id: number }
  >;
  getUserInfoByEmail: EzPzThunk<AdminDashboardStateModel, string>;
  clearUserLookup: EzPzAction<AdminDashboardStateModel>;
  getRecordingInfoById: EzPzThunk<
    AdminDashboardStateModel,
    { driveId: number | string; isPublic: boolean }
  >;
  updateHwVersionBatch: EzPzThunk<
    AdminDashboardStateModel,
    { page: number; perPage: number; lastProcessedId?: number },
    any,
    any,
    Promise<{
      updated: number;
      completed: boolean;
      progress_percentage: number;
      last_processed_id: number;
    }>
  >;
  syncStudioEmailVerificationToKeycloak: EzPzThunk<
    AdminDashboardStateModel,
    { page: number; perPage: number; lastProcessedId?: number },
    any,
    any,
    Promise<{
      updated: number;
      completed: boolean;
      progress_percentage: number;
    }>
  >;
  syncUserWithUploader: EzPzThunk<
    AdminDashboardStateModel,
    { identity_id: string }
  >;
  syncOrgWithUploader: EzPzThunk<
    AdminDashboardStateModel,
    { tenant_id: string }
  >;
  compareUsersOrgsWithUploader: EzPzThunk<AdminDashboardStateModel, void>;
  resetHwVersionProgress: EzPzAction<AdminDashboardStateModel>;
  resetSyncVerifiedEmailProgress: EzPzAction<AdminDashboardStateModel>;
  clearRecordingLookup: EzPzAction<AdminDashboardStateModel>;
  updateStoragePreferences: EzPzThunk<
    AdminDashboardStateModel,
    { organization_id: number; regions: string[] }
  >;
  reprocessAnyRecording: EzPzThunk<
    AdminDashboardStateModel,
    { drive_id: number }
  >;
  getSlamStatus: EzPzThunk<
    AdminDashboardStateModel,
    { organization_id: number },
    any,
    any,
    Promise<SlamStatusResponse>
  >;
  setSlamStatus: EzPzThunk<
    AdminDashboardStateModel,
    { organization_id: number; slam_enabled: boolean },
    any,
    any,
    Promise<SlamStatusResponse>
  >;
  reloadSensorMapping: EzPzThunk<
    AdminDashboardStateModel,
    void,
    any,
    any,
    Promise<ReloadSensorMappingResponse>
  >;
}

export const model: AdminDashboardStateModel = {
  ...defaultState,

  setSummaryTimeRange: action((state, timeRange) => {
    localStorage.setItem('timeRange', timeRange.toString());
    state.summaryTimeRange = timeRange;
  }),
  setOrgPage: action((state, page) => {
    state.orgPage = page;
  }),
  setOrgPageSize: action((state, pageSize) => {
    state.orgPageSize = pageSize;
  }),
  setUserLookupResult: action((state, userLookupResult) => {
    state.userLookupResult = userLookupResult;
  }),
  setRecordingLookupResult: action((state, recordingLookupResult) => {
    state.recordingLookupResult = recordingLookupResult;
  }),
  setIsLoadingUserLookup: action((state, isLoading) => {
    state.isLoadingUserLookup = isLoading;
  }),
  setIsLoadingRecordingLookup: action((state, isLoading) => {
    state.isLoadingRecordingLookup = isLoading;
  }),
  setHwVersionUpdateProgress: action((state, progress) => {
    state.hwVersionUpdateProgress = progress;
  }),
  setVerifiedEmailSyncProgress: action((state, progress) => {
    state.verifiedEmailSyncProgress = progress;
  }),
  setCompareUsersOrgsResult: action((state, result) => {
    state.compareUsersOrgsResult = result;
  }),
  clearUserLookup: action((state) => {
    state.userLookupResult = null;
    state.isLoadingUserLookup = false;
  }),
  clearRecordingLookup: action((state) => {
    state.recordingLookupResult = null;
    state.isLoadingRecordingLookup = false;
  }),
  updateOrgAllowedSpace: thunk(
    async (actions, { allowed_space, organization_id }) => {
      const path = `${BASE_ADMIN_DASHBOARD_API}/update_allowed_space`;

      try {
        const response = (await fetchWithAuthV2(path, {
          method: 'PATCH',
          fallbackErrorMessage: 'failed to update organization allowed space',
          body: JSON.stringify({
            'allowed-space': allowed_space,
            'user-org-id': organization_id,
          }),
        })) as {
          allowed_space: number;
          organization_id: number;
          space_used: number;
        };

        return response;
      } catch (err) {
        console.error('failed to update organization allowed space: ', err);
        Sentry.captureException(err, {
          tags: {
            section: 'AdminDashboardStateModel',
            thunk: 'updateOrgAllowedSpace',
          },
          extra: { allowed_space, organization_id },
        });
        throw err;
      }
    }
  ),
  getUserInfoByEmail: thunk(async (actions, email) => {
    // Clear previous results and set loading
    actions.clearUserLookup();
    actions.setIsLoadingUserLookup(true);

    const path = `${BASE_ADMIN_DASHBOARD_API}/get_user_info_by_email`;
    try {
      const response = (await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'failed to get user info by email',
        body: JSON.stringify({ email }),
      })) as UserLookupResponse;

      actions.setUserLookupResult(response);
    } catch (err) {
      console.error('failed to get user info by email: ', err);
      actions.setUserLookupResult(null);
      Sentry.captureException(err, {
        tags: {
          section: 'AdminDashboardStateModel',
          thunk: 'getUserInfoByEmail',
        },
        extra: { email },
      });
      throw err;
    } finally {
      actions.setIsLoadingUserLookup(false);
    }
  }),
  getRecordingInfoById: thunk(
    async (
      actions,
      { driveId, isPublic }: { driveId: number | string; isPublic: boolean }
    ) => {
      // Clear previous results and set loading
      actions.clearRecordingLookup();
      actions.setIsLoadingRecordingLookup(true);

      const payload = isPublic
        ? { public_uuid: driveId }
        : { drive_id: driveId };

      const path = `${BASE_ADMIN_DASHBOARD_API}/search_drive_by_id`;
      try {
        const response = (await fetchWithAuthV2(path, {
          method: 'POST',
          fallbackErrorMessage: 'failed to get recording info by ID',
          body: JSON.stringify(payload),
        })) as RawDriveInfoWithRegions;

        // Transform regions to extract only region strings
        const processedResponse: DriveInfoWithRegions = {
          ...response,
          regions: response.regions.map((region) => region.region),
        };

        actions.setRecordingLookupResult(processedResponse);
      } catch (err) {
        console.error('failed to get recording info by ID: ', err);
        actions.setRecordingLookupResult(null);
        Sentry.captureException(err, {
          tags: {
            section: 'AdminDashboardStateModel',
            thunk: 'getRecordingInfoById',
          },
          extra: { driveId, isPublic },
        });
        throw err;
      } finally {
        actions.setIsLoadingRecordingLookup(false);
      }
    }
  ),
  updateHwVersionBatch: thunk(
    async (actions, { page, perPage, lastProcessedId }) => {
      const path = `${BASE_ADMIN_DASHBOARD_API}/update_all_sensor_hw_revs`;

      try {
        const payload: any = {
          page,
          per_page: perPage,
          last_processed_id: lastProcessedId,
        };

        const response = (await fetchWithAuthV2(path, {
          method: 'POST',
          fallbackErrorMessage: 'failed to update hardware version',
          body: JSON.stringify(payload),
        })) as {
          updated: number;
          completed: boolean;
          progress_percentage: number;
          last_processed_id: number;
        };

        return response;
      } catch (err) {
        console.error('failed to update hardware version: ', err);
        Sentry.captureException(err, {
          tags: {
            section: 'AdminDashboardStateModel',
            thunk: 'updateHwVersion',
          },
        });
        throw err;
      }
    }
  ),
  syncStudioEmailVerificationToKeycloak: thunk(
    async (actions, { page, perPage }) => {
      const path = `${BASE_ADMIN_DASHBOARD_API}/verify_all_unverified_emails`;

      try {
        const payload: any = {
          page,
          per_page: perPage,
        };

        const response = (await fetchWithAuthV2(path, {
          method: 'POST',
          fallbackErrorMessage: 'failed to verify email addresses',
          body: JSON.stringify(payload),
        })) as {
          updated_keycloak_count: number;
          has_more: boolean;
          total_users: number;
          total_pages: number;
        };

        const result = {
          updated: response.updated_keycloak_count,
          completed: !response.has_more,
          progress_percentage: Math.min(
            100,
            Math.round(((page * perPage) / response.total_users) * 100)
          ),
        };
        return result;
      } catch (err) {
        console.error('failed to verify email addresses: ', err);
        Sentry.captureException(err, {
          tags: {
            section: 'AdminDashboardStateModel',
            thunk: 'syncStudioEmailVerificationToKeycloak',
          },
        });
        throw err;
      }
    }
  ),
  compareUsersOrgsWithUploader: thunk(async (actions) => {
    const path = `${BASE_ADMIN_DASHBOARD_API}/compare_users_orgs_with_uploader`;

    try {
      const response = (await fetchWithAuthV2(path, {
        method: 'GET',
        fallbackErrorMessage:
          'failed to compare users and organizations with uploader',
      })) as {
        missing_organizations_in_uploader: Array<{
          org_name: string;
          tenant_id: string;
        }>;
        missing_users_in_uploader: Array<{
          email: string;
          identity_id: string;
        }>;
      };
      actions.setCompareUsersOrgsResult(response);
    } catch (err) {
      console.error(
        'failed to compare users and organizations with uploader: ',
        err
      );
      Sentry.captureException(err, {
        tags: {
          section: 'AdminDashboardStateModel',
          thunk: 'compareUsersOrgsWithUploader',
        },
      });
      throw err;
    }
  }),
  syncUserWithUploader: thunk(async (actions, { identity_id }) => {
    const path = `${BASE_ADMIN_DASHBOARD_API}/notify_uploader_create_user`;

    try {
      await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'failed to sync user with uploader',
        body: JSON.stringify({ identity_id }),
      });
    } catch (err) {
      console.error('failed to sync user with uploader: ', err);
      Sentry.captureException(err, {
        tags: {
          section: 'AdminDashboardStateModel',
          thunk: 'syncUserWithUploader',
        },
      });
      throw err;
    }
  }),
  syncOrgWithUploader: thunk(async (actions, { tenant_id }) => {
    const path = `${BASE_ADMIN_DASHBOARD_API}/notify_uploader_create_organization`;

    try {
      await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'failed to sync org with uploader',
        body: JSON.stringify({ tenant_id }),
      });
    } catch (err) {
      console.error('failed to sync org with uploader: ', err);
      Sentry.captureException(err, {
        tags: {
          section: 'AdminDashboardStateModel',
          thunk: 'syncOrgWithUploader',
        },
      });
      throw err;
    }
  }),
  resetHwVersionProgress: action((state) => {
    state.hwVersionUpdateProgress = {
      totalUpdated: 0,
      currentPage: 0,
      isInProgress: false,
      progressPercentage: 0,
      lastProcessedId: 0,
    };
  }),
  resetSyncVerifiedEmailProgress: action((state) => {
    state.verifiedEmailSyncProgress = {
      totalUpdated: 0,
      currentPage: 0,
      isInProgress: false,
      progressPercentage: 0,
    };
  }),
  updateStoragePreferences: thunk(async (_, { organization_id, regions }) => {
    const path = `${BASE_ADMIN_DASHBOARD_API}/storage_preferences`;

    try {
      await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'failed to update storage preferences',
        body: JSON.stringify({
          organization_id,
          regions,
        }),
      });
    } catch (err) {
      console.error('failed to update storage preferences: ', err);
      Sentry.captureException(err, {
        tags: {
          section: 'AdminDashboardStateModel',
          thunk: 'updateStoragePreferences',
        },
        extra: { organization_id, regions },
      });
      throw err;
    }
  }),
  reprocessAnyRecording: thunk(async (_, { drive_id }) => {
    const path = `${BASE_ADMIN_DASHBOARD_API}/reprocess_drive`;

    try {
      await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'failed to reprocess drive from dashboard',
        body: JSON.stringify({
          drive_id,
        }),
      });
    } catch (err) {
      console.error('failed to reprocess drive from dashboard: ', err);
      Sentry.captureException(err, {
        tags: {
          section: 'AdminDashboardStateModel',
          thunk: 'reprocessAnyRecording',
        },
        extra: { drive_id },
      });
      throw err;
    }
  }),
  getSlamStatus: thunk(async (_, { organization_id }) => {
    const path = `${BASE_ADMIN_DASHBOARD_API}/get_slam_status`;

    try {
      const response = (await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'failed to get SLAM status',
        body: JSON.stringify({
          organization_id,
        }),
      })) as SlamStatusResponse;
      return response;
    } catch (err) {
      console.error('failed to get SLAM status: ', err);
      Sentry.captureException(err, {
        tags: {
          section: 'AdminDashboardStateModel',
          thunk: 'getSlamStatus',
        },
        extra: { organization_id },
      });
      throw err;
    }
  }),
  setSlamStatus: thunk(async (_, { organization_id, slam_enabled }) => {
    const path = `${BASE_ADMIN_DASHBOARD_API}/set_slam_status`;

    try {
      const response = (await fetchWithAuthV2(path, {
        method: 'PATCH',
        fallbackErrorMessage: 'failed to set SLAM status',
        body: JSON.stringify({
          organization_id,
          slam_enabled,
        }),
      })) as SlamStatusResponse;
      return response;
    } catch (err) {
      console.error('failed to set SLAM status: ', err);
      Sentry.captureException(err, {
        tags: {
          section: 'AdminDashboardStateModel',
          thunk: 'setSlamStatus',
        },
        extra: { organization_id, slam_enabled },
      });
      throw err;
    }
  }),
  reloadSensorMapping: thunk(async () => {
    const path = `${BASE_ADMIN_DASHBOARD_API}/reload_sensor_mapping`;

    try {
      const response = (await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'failed to reload sensor mapping',
      })) as ReloadSensorMappingResponse;
      return response;
    } catch (err) {
      console.error('failed to reload sensor mapping: ', err);
      Sentry.captureException(err, {
        tags: {
          section: 'AdminDashboardStateModel',
          thunk: 'reloadSensorMapping',
        },
      });
      throw err;
    }
  }),
};
